import { getHistoryData } from "../../data/ramotswe";
import {
  getCurrentObservatoryName,
  changeBinnedTimeSeriesUTCTimeToLocalTime,
  createNullBinnedTimeSeriesData,
  getXLims,
  getBinnedXValues,
  getWidestXLimits,
  ensureYKeyPresence,
  trimNullEdges,
} from "../../utils";
import { DashboardTimeLineGraph } from "../../graphs/base_graphs";
import { DateTime } from "../../imports";

export default async function initializeBatterySolar() {
  // define how the data is fetched
  async function getData() {
    const observatoryName = getCurrentObservatoryName();
    const endDateTime = DateTime.now().setZone("UTC");
    const startDateTime = endDateTime.minus({ days: 2 });

    let solarChargeRateData = await getHistoryData(
      observatoryName,
      "solar/solar_charge_rate",
      startDateTime,
      endDateTime
    );
    let batteryChargePercentData = await getHistoryData(
      observatoryName,
      "solar/battery_soc",
      startDateTime,
      endDateTime
    );

    if (solarChargeRateData === null) {
      solarChargeRateData = createNullBinnedTimeSeriesData(
        "solar_charge_rate",
        startDateTime,
        endDateTime
      );
    }
    if (batteryChargePercentData === null) {
      batteryChargePercentData = createNullBinnedTimeSeriesData(
        "battery_state_of_charge_0",
        startDateTime,
        endDateTime
      );
    }

    // add second battery
    batteryChargePercentData.forEach((row) => {
      if (!row.hasOwnProperty("battery_state_of_charge_1")) {
        row["battery_state_of_charge_1"] = null;
      }
    });

    // process data
    solarChargeRateData =
      changeBinnedTimeSeriesUTCTimeToLocalTime(solarChargeRateData);
    solarChargeRateData = ensureYKeyPresence(solarChargeRateData, [
      "solar_charge_rate",
    ]);
    solarChargeRateData = trimNullEdges(solarChargeRateData, [
      "solar_charge_rate",
    ]);

    batteryChargePercentData = changeBinnedTimeSeriesUTCTimeToLocalTime(
      batteryChargePercentData
    );
    batteryChargePercentData = ensureYKeyPresence(batteryChargePercentData, [
      "battery_state_of_charge_0",
      "battery_state_of_charge_1",
    ]);
    batteryChargePercentData = trimNullEdges(batteryChargePercentData, [
      "battery_state_of_charge_0",
      "battery_state_of_charge_1",
    ]);

    // get x limits
    const solarXlims = getXLims(getBinnedXValues(solarChargeRateData));
    const batteryXlims = getXLims(getBinnedXValues(batteryChargePercentData));
    const xLims = getWidestXLimits([solarXlims, batteryXlims]);

    return [[solarChargeRateData, batteryChargePercentData], xLims];
  }

  // set up the initial data and axes
  const [initialData, xLims] = await getData();

  const solarChargeRateGraph = new DashboardTimeLineGraph(
    "#battery-charge-graph",
    "Time",
    "Battery Charge [%]",
    "Solar Charge Rate [W]"
  );

  // set the graph parameters for consistency in initialization & refresh
  function makeBarGraphParams(allGraphData, hideSVG, xLims) {
    return [
      allGraphData[0], // pull just the solar data
      "time_bin_edges",
      "solar_charge_rate",
      {
        secondAxis: true,
        transition: true,
        staticYlims: [0, 2500],
        hideSVG: hideSVG,
        staticXlims: xLims,
      },
    ];
  }
  function makeLineGraphParams(allGraphData, hideSVG, xLims) {
    return [
      allGraphData[1], // pull just the battery data
      "time_bin_center",
      "battery_state_of_charge_0",
      {
        secondAxis: false,
        transition: true,
        staticYlims: [0, 100],
        hideSVG: hideSVG,
        staticXlims: xLims,
      },
    ];
  }
  function makeLine2GraphParams(allGraphData, hideSVG, xLims) {
    return [
      allGraphData[1], // pull just the battery data
      "time_bin_center",
      "battery_state_of_charge_1",
      {
        secondAxis: false,
        transition: true,
        staticYlims: [0, 100],
        extraClasses: ["battery2"],
        hideSVG: hideSVG,
        staticXlims: xLims,
      },
    ];
  }

  // populate the graphs, saving the names of them
  // since hideSVG is true, nothing will be visible in the graph area
  const barAxesKey = solarChargeRateGraph.makeBarGraph(
    ...makeBarGraphParams(initialData, true, xLims)
  );
  const lineAxesKey = solarChargeRateGraph.makeLineGraph(
    ...makeLineGraphParams(initialData, true, xLims)
  );
  const line2AxesKey = solarChargeRateGraph.makeLineGraph(
    ...makeLine2GraphParams(initialData, true, xLims)
  );
  // show the loading screen so something is there
  solarChargeRateGraph.showLoading();

  // define the refresh function
  async function refresh() {
    solarChargeRateGraph.showLoading();
    const [newData, xLims] = await getData();

    // updateData has the same format as make*Graph, but with the key as param 1
    solarChargeRateGraph.updateData(
      barAxesKey,
      ...makeBarGraphParams(newData, false, xLims)
    );
    solarChargeRateGraph.updateData(
      line2AxesKey,
      ...makeLine2GraphParams(newData, false, xLims)
    );
    solarChargeRateGraph.updateData(
      lineAxesKey,
      ...makeLineGraphParams(newData, false, xLims)
    );
  }

  return refresh;
}
